import { IFilterElement } from "@saleor/components/Filter";
import { UseSearchResult } from "@saleor/hooks/makeSearch";
import { maybe } from "@saleor/misc";
import { removeBase64Name } from "@saleor/reports/utils/misc";
import {
  SearchMalls,
  SearchMallsVariables
} from "@saleor/searches/types/SearchMalls";
import { InitialSellerOrderFilterData_malls_edges_node } from "@saleor/sellerOrders/types/InitialSellerOrderFilterData";
import {
  createFilterTabUtils,
  createFilterUtils,
  dedupeFilter,
  getGteLteVariables,
  getMinMaxQueryParam,
  getMultipleValueQueryParam
} from "@saleor/utils/filters";
import moment from "moment-timezone";

import {
  SellerOrdersFilterKeys,
  SellerOrdersFilterOpts
} from "../../components/SellerOrdersListPage/filters";
import {
  SellerOrdersListUrlFilters,
  SellerOrdersListUrlFiltersEnum,
  SellerOrdersListUrlFiltersWithMultipleValues,
  SellerOrdersListUrlQueryParams
} from "../../url";

export const SELLER_ORDERS_FILTERS_KEY = "SellerOrdersFilter";

export function getFilterOpts(
  params: SellerOrdersListUrlFilters,
  malls: {
    initial: InitialSellerOrderFilterData_malls_edges_node[];
    search: UseSearchResult<SearchMalls, SearchMallsVariables>;
  }
): SellerOrdersFilterOpts {
  return {
    completedAt: {
      active: maybe(
        () =>
          [params.orderCompleteDateFrom, params.orderCompleteDateTo].some(
            field => field !== undefined
          ),
        false
      ),
      value: {
        max: maybe(() => params.orderCompleteDateTo, ""),
        min: maybe(() => params.orderCompleteDateFrom, "")
      }
    },
    mall: {
      active: !!params.mall,
      choices: maybe(
        () =>
          malls.search.result.data.search.edges.map(edge => ({
            label: edge.node.name,
            value: edge.node.id
          })),
        []
      ),
      displayValues: !!params.mall
        ? maybe(
            () =>
              malls.initial.map(mall => ({
                label: mall.name,
                value: mall.id
              })),
            []
          )
        : [],
      hasMore: maybe(
        () => malls.search.result.data.search.pageInfo.hasNextPage,
        false
      ),
      initialSearch: "",
      loading: malls.search.result.loading,
      onFetchMore: malls.search.loadMore,
      onSearchChange: malls.search.search,
      value: maybe(() => dedupeFilter(params.mall), [])
    },
    orderDate: {
      active: maybe(
        () =>
          [params.orderDateFrom, params.orderDateTo].some(
            field => field !== undefined
          ),
        false
      ),
      value: {
        max: maybe(() => params.orderDateTo, ""),
        min: maybe(() => params.orderDateFrom, "")
      }
    },
    orderStatus: {
      active: !!params.orderStatus,
      value: maybe(() => dedupeFilter(params.orderStatus), null)
    },
    shippingMethodName: {
      active: !!params.deliveryType,
      value: maybe(() => dedupeFilter(params.deliveryType), null)
    }
  };
}

export function getFilterVariables(params: SellerOrdersListUrlFilters) {
  const newDate = new Date();
  const endDay = newDate.getDate();
  const endMonth = newDate.getMonth() + 1;
  const endYear = newDate.getFullYear();

  const startDate = moment(newDate.setMonth(newDate.getMonth() - 3)).format(
    "YYYY-MM-DD"
  );
  const endDate = moment(`${endYear}-${endMonth}-${endDay}`)
    .add(1, "days")
    .format("YYYY-MM-DD");

  return {
    completedAt: getGteLteVariables({
      gte: params.orderCompleteDateFrom
        ? moment(`${params.orderCompleteDateFrom} 00:00:00`).format(
            "YYYY-MM-DD HH:mm:ssZZ"
          )
        : null,
      lte: params.orderCompleteDateTo
        ? moment(`${params.orderCompleteDateTo} 00:00:00`)
            .add(1, "days")
            .format("YYYY-MM-DD HH:mm:ssZZ")
        : null
    }),
    mallId: removeBase64Name(
      "Mall:",
      params.mall !== undefined ? params.mall : []
    ),
    orderDate: getGteLteVariables({
      gte: params.orderDateFrom
        ? moment(`${params.orderDateFrom} 00:00:00`).format(
            "YYYY-MM-DD HH:mm:ssZZ"
          )
        : !params.orderDateFrom &&
          (params.orderCompleteDateFrom || params.orderCanceledFrom)
        ? null
        : moment(`${startDate} 00:00:00`).format("YYYY-MM-DD HH:mm:ssZZ"),

      lte: params.orderDateTo
        ? moment(`${params.orderDateTo} 00:00:00`)
            .add(1, "days")
            .format("YYYY-MM-DD HH:mm:ssZZ")
        : !params.orderDateTo &&
          (params.orderCompleteDateTo || params.orderCanceledTo)
        ? null
        : endDate
    }),
    orderStatus: maybe(() => params.orderStatus, null),
    search: params.query,
    shippingMethodName: maybe(() => params.deliveryType, null),
    storeOrderStatus: maybe(() => params.orderStatus, null)
  };
}

export function getFilterQueryParam(
  filter: IFilterElement<SellerOrdersFilterKeys>
): SellerOrdersListUrlFilters {
  const { name } = filter;

  switch (name) {
    case SellerOrdersFilterKeys.mall:
      return getMultipleValueQueryParam(
        filter,
        SellerOrdersListUrlFiltersWithMultipleValues.mall
      );
    case SellerOrdersFilterKeys.orderDate:
      return getMinMaxQueryParam(
        filter,
        SellerOrdersListUrlFiltersEnum.orderDateFrom,
        SellerOrdersListUrlFiltersEnum.orderDateTo
      );
    case SellerOrdersFilterKeys.completedAt:
      return getMinMaxQueryParam(
        filter,
        SellerOrdersListUrlFiltersEnum.orderCompleteDateFrom,
        SellerOrdersListUrlFiltersEnum.orderCompleteDateTo
      );
    case SellerOrdersFilterKeys.orderStatus:
      return getMultipleValueQueryParam(
        filter,
        SellerOrdersListUrlFiltersWithMultipleValues.orderStatus
      );
    case SellerOrdersFilterKeys.shippingMethodName:
      return getMultipleValueQueryParam(
        filter,
        SellerOrdersListUrlFiltersWithMultipleValues.deliveryType
      );
  }
}

export const {
  deleteFilterTab,
  getFilterTabs,
  saveFilterTab
} = createFilterTabUtils<SellerOrdersListUrlFilters>(SELLER_ORDERS_FILTERS_KEY);

export const { areFiltersApplied, getActiveFilters } = createFilterUtils<
  SellerOrdersListUrlQueryParams,
  SellerOrdersListUrlFilters
>({
  ...SellerOrdersListUrlFiltersEnum,
  ...SellerOrdersListUrlFiltersWithMultipleValues
});
